import React, { useCallback } from 'react';
import { Dropdown } from '@packages/ui/composites/Dropdown';
import { DataTable } from '@packages/ui/patterns/DataTable';
import type { EachRowAction, FormEachNode } from '../../../types/layout';
import { ActionRegistry } from '@packages/table-builder/registry';
import { useFormEngineState } from '../../../context/FormEngineContext';

interface FormEachRowActionsButtonProps {
  node: FormEachNode;
  index: number;
  rowValues?: Record<string, unknown>;
}

/**
 * Per-row action menu for `type: each` nodes with `rowActions` configured.
 * Mirrors DynamicTableV2's ActionRenderer — renders a three-dot Dropdown
 * and dispatches to OOP AbstractAction classes via ActionRegistry.
 *
 * - `onlyInMode` gates per action to 'create' | 'edit' | 'view'.
 * - The action receives ctx.row = rowValues and ctx.params = { rowIndex }.
 */
export const FormEachRowActionsButton = React.memo(function FormEachRowActionsButton({
  node,
  index,
  rowValues,
}: FormEachRowActionsButtonProps): React.ReactElement | null {
  const { meta, params } = useFormEngineState();

  const currentMode = (meta?.mode as string | undefined) ?? 'create';

  const visibleActions = (node.rowActions ?? []).filter((a: EachRowAction) => {
    if (a.onlyInMode && a.onlyInMode !== currentMode) return false;
    return true;
  });

  const handleAction = useCallback(
    async (actionEntry: EachRowAction) => {
      // Spread all form-engine params (e.g. { id, planId, ... }) into the action ctx
      // so action classes can resolve parent entity IDs via ctx.params without any
      // domain-specific key names being hardcoded here. rowIndex is appended on top.
      await ActionRegistry.executeAction(actionEntry.actionId, {
        pk: String(rowValues?.id ?? rowValues?.purchasePeriodId ?? index),
        row: rowValues ?? {},
        params: { ...params, rowIndex: String(index) },
        refreshTable: () => {},
      });
    },
    [rowValues, index, params],
  );

  if (visibleActions.length === 0) return null;

  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild>
        <DataTable.ActionTrigger
          data-testid={`each-row-actions-${node.name}-${index}`}
        />
      </Dropdown.Trigger>
      <Dropdown.Content>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {visibleActions.map((actionEntry) => (
            <li key={actionEntry.actionId}>
              <Dropdown.Item
                onSelect={() => { void handleAction(actionEntry); }}
                color={actionEntry.danger ? 'danger' : 'default'}
              >
                {actionEntry.label}
              </Dropdown.Item>
            </li>
          ))}
        </ul>
      </Dropdown.Content>
    </Dropdown.Root>
  );
});
